import { jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from "react";
import {
  Button,
  Menu,
  MenuItem,
  MenuDivider,
  Popover,
  Position,
  Dialog,
  FormGroup,
  InputGroup,
  HTMLSelect,
  Tag,
  Intent,
  Spinner,
  Classes
} from "@blueprintjs/core";
import axios from "axios";
export const CompanySwitcher = ({ onCompanyChanged }) => {
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(
    localStorage.getItem("ove_company_id") || "default-company"
  );
  const [activeRole, setActiveRole] = useState(
    localStorage.getItem("ove_user_role") || "editor"
  );
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState(null);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const fetchAuthInfo = async () => {
    try {
      const res = await axios.get("/api/auth/me");
      if (res.data.authenticated) {
        setCompanies(res.data.companies || []);
        setActiveCompanyId(res.data.company_id || "default-company");
        setActiveRole(res.data.role || "editor");
        setCurrentUser(res.data.user || null);
        localStorage.setItem("ove_company_id", res.data.company_id || "default-company");
        localStorage.setItem("ove_user_role", res.data.role || "editor");
        axios.defaults.headers.common["X-Company-Id"] = res.data.company_id || "default-company";
      }
    } catch (e) {
      console.warn("Could not fetch auth/me", e);
    }
  };
  useEffect(() => {
    fetchAuthInfo();
  }, []);
  const handleSwitchCompany = async (targetCompanyId) => {
    if (targetCompanyId === activeCompanyId) return;
    setLoading(true);
    try {
      const res = await axios.post("/api/auth/switch-company", {
        company_id: targetCompanyId
      });
      if (res.data.success) {
        localStorage.setItem("ove_token", res.data.token);
        localStorage.setItem("ove_company_id", res.data.company_id);
        localStorage.setItem("ove_user_role", res.data.role);
        axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
        axios.defaults.headers.common["X-Company-Id"] = res.data.company_id;
        setActiveCompanyId(res.data.company_id);
        setActiveRole(res.data.role);
        if (onCompanyChanged) {
          onCompanyChanged(res.data.company_id);
        } else {
          window.location.reload();
        }
      }
    } catch (e) {
      console.error("Failed to switch company", e);
    } finally {
      setLoading(false);
    }
  };
  const openInviteModal = async () => {
    setIsInviteOpen(true);
    setInviteMessage(null);
    setLoadingMembers(true);
    try {
      const res = await axios.get(`/api/auth/companies/${activeCompanyId}/members`);
      setMembers(res.data.members || []);
    } catch (e) {
      console.warn("Failed to load members", e);
    } finally {
      setLoadingMembers(false);
    }
  };
  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviteLoading(true);
    setInviteMessage(null);
    try {
      const res = await axios.post(`/api/auth/companies/${activeCompanyId}/members`, {
        email: inviteEmail,
        role: inviteRole
      });
      if (res.data.success) {
        setInviteMessage({ intent: Intent.SUCCESS, text: `Successfully added ${inviteEmail}` });
        setInviteEmail("");
        const mRes = await axios.get(`/api/auth/companies/${activeCompanyId}/members`);
        setMembers(mRes.data.members || []);
      }
    } catch (e2) {
      const errMsg = e2.response?.data?.description || e2.response?.data?.error || "Failed to invite member";
      setInviteMessage({ intent: Intent.DANGER, text: errMsg });
    } finally {
      setInviteLoading(false);
    }
  };
  const currentCompany = companies.find((c) => c.id === activeCompanyId) || {
    name: activeCompanyId === "default-company" ? "Delve Therapeutics" : activeCompanyId
  };
  const canManageMembers = activeRole === "owner" || activeRole === "admin";
  const companyMenu = /* @__PURE__ */ jsxs(Menu, { children: [
    /* @__PURE__ */ jsx("li", { className: "bp3-menu-header", children: /* @__PURE__ */ jsx("h6", { className: "bp3-heading", children: "Organizations" }) }),
    companies.map((c) => /* @__PURE__ */ jsx(
      MenuItem,
      {
        icon: c.id === activeCompanyId ? "small-tick" : "blank",
        text: c.name,
        label: /* @__PURE__ */ jsx(Tag, { minimal: true, round: true, intent: c.role === "owner" ? Intent.WARNING : Intent.NONE, children: c.role }),
        onClick: () => handleSwitchCompany(c.id)
      },
      c.id
    )),
    companies.length === 0 && /* @__PURE__ */ jsx(MenuItem, { disabled: true, text: "Delve Therapeutics" }),
    /* @__PURE__ */ jsx(MenuDivider, {}),
    canManageMembers && /* @__PURE__ */ jsx(
      MenuItem,
      {
        icon: "people",
        text: "Organization Members...",
        onClick: openInviteModal
      }
    )
  ] });
  return /* @__PURE__ */ jsxs("div", { style: { display: "inline-flex", alignItems: "center", marginRight: "10px" }, children: [
    /* @__PURE__ */ jsx(Popover, { content: companyMenu, position: Position.BOTTOM_LEFT, children: /* @__PURE__ */ jsx(
      Button,
      {
        minimal: true,
        icon: "office",
        rightIcon: "caret-down",
        loading,
        text: /* @__PURE__ */ jsxs("span", { children: [
          /* @__PURE__ */ jsx("strong", { children: currentCompany.name }),
          " ",
          /* @__PURE__ */ jsx(Tag, { minimal: true, round: true, intent: activeRole === "owner" ? Intent.WARNING : Intent.NONE, style: { marginLeft: 5 }, children: activeRole })
        ] })
      }
    ) }),
    /* @__PURE__ */ jsxs(
      Dialog,
      {
        isOpen: isInviteOpen,
        onClose: () => setIsInviteOpen(false),
        title: `Manage Organization: ${currentCompany.name}`,
        icon: "people",
        style: { width: 560 },
        children: [
          /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_BODY, children: [
            inviteMessage && /* @__PURE__ */ jsx("div", { style: { marginBottom: 15 }, children: /* @__PURE__ */ jsx(Tag, { fill: true, large: true, intent: inviteMessage.intent, children: inviteMessage.text }) }),
            /* @__PURE__ */ jsx("h5", { className: "bp3-heading", style: { marginBottom: 10 }, children: "Invite / Add Member" }),
            /* @__PURE__ */ jsxs("form", { onSubmit: handleInviteSubmit, style: { display: "flex", gap: "8px", alignItems: "flex-end", marginBottom: 20 }, children: [
              /* @__PURE__ */ jsx(FormGroup, { label: "Email Address", style: { flexGrow: 1, marginBottom: 0 }, children: /* @__PURE__ */ jsx(
                InputGroup,
                {
                  placeholder: "colleague@example.com",
                  value: inviteEmail,
                  onChange: (e) => setInviteEmail(e.target.value),
                  required: true
                }
              ) }),
              /* @__PURE__ */ jsx(FormGroup, { label: "Role", style: { width: 120, marginBottom: 0 }, children: /* @__PURE__ */ jsx(
                HTMLSelect,
                {
                  value: inviteRole,
                  onChange: (e) => setInviteRole(e.target.value),
                  options: [
                    { label: "Editor", value: "editor" },
                    { label: "Admin", value: "admin" },
                    { label: "Viewer", value: "viewer" },
                    { label: "Owner", value: "owner" }
                  ]
                }
              ) }),
              /* @__PURE__ */ jsx(
                Button,
                {
                  type: "submit",
                  intent: Intent.PRIMARY,
                  icon: "add",
                  text: "Add",
                  loading: inviteLoading
                }
              )
            ] }),
            /* @__PURE__ */ jsx("h5", { className: "bp3-heading", style: { marginTop: 20, marginBottom: 10 }, children: "Current Members" }),
            loadingMembers ? /* @__PURE__ */ jsx("div", { style: { textAlign: "center", padding: 20 }, children: /* @__PURE__ */ jsx(Spinner, { size: 24 }) }) : /* @__PURE__ */ jsx("div", { style: { maxHeight: 220, overflowY: "auto", border: "1px solid #ddd", borderRadius: 4 }, children: /* @__PURE__ */ jsxs("table", { className: "bp3-html-table bp3-html-table-striped bp3-html-table-condensed", style: { width: "100%" }, children: [
              /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
                /* @__PURE__ */ jsx("th", { children: "Email" }),
                /* @__PURE__ */ jsx("th", { children: "Role" }),
                /* @__PURE__ */ jsx("th", { children: "Status" })
              ] }) }),
              /* @__PURE__ */ jsxs("tbody", { children: [
                members.map((m) => /* @__PURE__ */ jsxs("tr", { children: [
                  /* @__PURE__ */ jsx("td", { children: m.email }),
                  /* @__PURE__ */ jsx("td", { children: /* @__PURE__ */ jsx(Tag, { minimal: true, round: true, intent: m.role === "owner" ? Intent.WARNING : Intent.NONE, children: m.role }) }),
                  /* @__PURE__ */ jsx("td", { children: /* @__PURE__ */ jsx(Tag, { minimal: true, intent: m.status === "active" ? Intent.SUCCESS : Intent.WARNING, children: m.status }) })
                ] }, m.membership_id || m.user_id)),
                members.length === 0 && /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { colSpan: 3, style: { textAlign: "center", color: "#888" }, children: "No members found" }) })
              ] })
            ] }) })
          ] }),
          /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: /* @__PURE__ */ jsx(Button, { onClick: () => setIsInviteOpen(false), children: "Done" }) }) })
        ]
      }
    )
  ] });
};
export default CompanySwitcher;
